import { useState } from "react";
import { GuidanceMatrixDialog } from "@/components/dashboard/GuidanceMatrixDialog";
import { ReservationBadge, reservationSentence } from "@/components/shared/ReservationBadge";
import { type MonthGuidance, useGuidance } from "@/hooks/use-guidance";
import { availabilityPercent, guidanceTier } from "@/lib/guidance-tier";
import { reservationCoversMonth, reservedHours } from "@/lib/resource-reservation";

// Reuse the existing fit-tier palette — same scale as the picker strip (WI-002 AC).
const TIER_VARS: Record<ReturnType<typeof guidanceTier>, { bg: string; fg: string }> = {
  low: { bg: "var(--fit-strong-bg)", fg: "var(--fit-strong)" },
  moderate: { bg: "var(--fit-moderate-bg)", fg: "var(--fit-moderate)" },
  high: { bg: "var(--fit-at-risk-bg)", fg: "var(--fit-at-risk)" },
  none: { bg: "var(--muted)", fg: "var(--muted-foreground)" },
};

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** An in-force Account reservation, as the Outlook needs it. */
export interface OutlookReservation {
  accountName: string;
  /** Last day of the reservation, `YYYY-MM-DD`, inclusive. null = open-ended. */
  until: string | null;
}

function OutlookCell({
  m,
  reservation,
  onClick,
}: {
  m: MonthGuidance;
  reservation: OutlookReservation | null;
  onClick?: () => void;
}) {
  const tier = guidanceTier(m.guidanceHours, m.capacityHours);
  const avail = availabilityPercent(m.guidanceHours, m.capacityHours);
  // A reservation turns this month's open time into time reserved for the Account — it is no
  // longer "free". Overload stays red: that is the more urgent read, and nothing is
  // left to reserve anyway.
  const isReserved = reservation !== null && reservationCoversMonth(reservation, m.monthStart);
  const reservedHrs = isReserved ? reservedHours(m.guidanceHours, m.capacityHours) : null;
  const { bg, fg } =
    isReserved && tier !== "high"
      ? { bg: "var(--reserved-bg)", fg: "var(--reserved)" }
      : TIER_VARS[tier];
  const label = MONTH_ABBR[m.month - 1] ?? "";
  const reservedTooltip =
    isReserved && reservation !== null
      ? ` · ${reservedHrs === null ? "open time unknown" : `${Math.round(reservedHrs)}h`} reserved for ${reservation.accountName}`
      : "";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 cursor-pointer flex-col items-center gap-0.5 rounded-md px-2 py-2 text-center transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        m.isCurrent ? "ring-2 ring-brand-500" : ""
      }`}
      style={{ backgroundColor: bg, color: fg }}
      title={`${label} ${m.year}${m.isCurrent ? " (current month)" : ""} · guidance ${
        m.guidanceHours ?? "—"
      }h / capacity ${m.capacityHours ?? "—"}h${reservedTooltip} — click for the Solution breakdown`}
    >
      <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</span>
      <span className="font-display text-lg font-bold leading-none">
        {m.guidanceHours === null ? "–" : Math.round(m.guidanceHours)}
        <span className="text-xs font-medium opacity-70">h</span>
      </span>
      <span className="text-sm font-semibold leading-none">
        {isReserved
          ? reservedHrs === null
            ? "reserved"
            : `${Math.round(reservedHrs)}h reserved`
          : avail === null
            ? "—"
            : `${avail}% free`}
      </span>
      <span className="text-xs leading-none opacity-60">
        {m.capacityHours === null ? "no cap" : `of ${Math.round(m.capacityHours)}h`}
      </span>
    </button>
  );
}

/**
 * Read-only monthly guidance + availability for a Resource (current + next 3),
 * sourced from NOS.
 *
 * Each tile is a button opening `GuidanceMatrixDialog` — the Solutions × months
 * breakdown that answers "which Solutions is this guidance for?", extended 3 months
 * back so past logged hours sit beside planned ones. The strip itself stays
 * person-level (guidance vs capacity); only the drill-down needs the per-Demand
 * endpoint, so the heavier read is deferred until a tile is clicked.
 */
export function GuidanceOutlook({
  resourceId,
  resourceName = "this Resource",
  reservation = null,
}: {
  resourceId: string;
  resourceName?: string;
  /**
   * The Resource's in-force Account reservation (docs/specs/resource-reservation.md). Months it
   * covers show their open time as reserved for that Account instead of "% free".
   */
  reservation?: OutlookReservation | null;
}) {
  const { byResource, isLoading } = useGuidance();
  const months = byResource.get(resourceId);
  const [matrixOpen, setMatrixOpen] = useState(false);

  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Guidance Outlook
        </div>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {reservation ? (
            <ReservationBadge accountName={reservation.accountName} until={reservation.until} />
          ) : null}
          from NOS · hrs / availability
        </span>
      </div>
      {isLoading ? (
        <div className="rounded-md border border-border bg-card p-3 text-sm text-muted-foreground">
          Loading guidance…
        </div>
      ) : !months || months.length === 0 ? (
        <div className="rounded-md border border-border bg-card p-3 text-sm italic text-muted-foreground">
          No NOS guidance for this Resource.
        </div>
      ) : (
        <>
          <div className="flex gap-1.5 rounded-md border border-border bg-card p-2">
            {months.slice(0, 4).map((m) => (
              <OutlookCell
                key={m.monthStart}
                m={m}
                reservation={reservation}
                onClick={() => setMatrixOpen(true)}
              />
            ))}
          </div>
          <div className="mt-1 text-center text-xs text-muted-foreground">
            {reservation
              ? `${reservationSentence(reservation.accountName, reservation.until)} — reserved = capacity not already in NOS guidance. Click a month for the breakdown.`
              : "Click a month for the Solution breakdown"}
          </div>
        </>
      )}
      <GuidanceMatrixDialog
        open={matrixOpen}
        onOpenChange={setMatrixOpen}
        resourceId={resourceId}
        resourceName={resourceName}
      />
    </section>
  );
}
